import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AtSign, CheckSquare, ShieldAlert, Search, X } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useGetTaskBoard,
  useResolveTaskRef,
  useGetTaskIngestRuns,
  useGetTaskLinkOptions,
  useGetTaskPeople,
  useSetTaskLinks,
  useUpdateTask,
} from '../hooks/backend/tasks'
import { toast } from '../lib/shadcn/sonner'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../lib/shadcn/alert-dialog'
import { TaskDateFilters } from '../components/tasks/TaskDateFilters'
import { Button } from '../lib/shadcn/button'
import { TooltipProvider } from '../lib/shadcn/tooltip'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { TaskBoard } from '../components/tasks/TaskBoard'
import { TaskPanel } from '../components/tasks/TaskPanel'
import { TaskPeople } from '../components/tasks/TaskPeople'
import { MeetingRuns } from '../components/tasks/MeetingRuns'
import { NewTaskDialog } from '../components/tasks/NewTaskDialog'
import {
  taskRowConfirmed,
  unappliedFields,
  EMPTY_DATE_FILTER,
  backlogHeldCount,
  dateFilterParams,
  type TaskDateFilter,
} from '../utils/tasks'
import { runConfirmed } from '../hooks/confirmedWrites'
import type {
  BoardGroup,
  IngestRun,
  LinkKind,
  TaskLinkOption,
  TaskPerson,
  TaskRow,
} from '../utils/tasks'

type View = 'board' | 'people' | 'meetings'

const VIEWS: { value: View; label: string }[] = [
  { value: 'board', label: 'Board' },
  { value: 'people', label: 'By person' },
  { value: 'meetings', label: 'Meetings' },
]

export default function Tasks() {
  const { loading: accessLoading, canView, isAdmin, caption } = useCapabilities()

  const [view, setView] = useState<View>('board')
  const [search, setSearch] = useState('')
  const [assignees, setAssignees] = useState<string[]>([])
  const [onlyOverdue, setOnlyOverdue] = useState(false)
  const [onlyConflicts, setOnlyConflicts] = useState(false)
  // Assigned to me, or naming me. One filter and not two, because the question
  // people ask is "what has my name on it", and on this board a mention is often
  // how work is handed over before anybody changes the assignee. The row itself
  // says which of the two it is; see the "names you" badge in TaskBoard.
  const [namesMe, setNamesMe] = useState(false)
  const [dateFilter, setDateFilter] = useState<TaskDateFilter>(EMPTY_DATE_FILTER)

  // Confirming a close over open subtasks.
  //
  // Promise-based rather than a boolean flag, because `save` is the single choke
  // point for every field change on this board and has to be able to await the
  // answer and then simply not write. A flag would mean the write had already
  // gone or a second code path existed for this one case.
  const confirmResolve = useRef<((ok: boolean) => void) | null>(null)
  const [closeWarning, setCloseWarning] = useState<{ title: string; open: number; total: number } | null>(
    null,
  )

  const answerCloseWarning = (ok: boolean) => {
    setCloseWarning(null)
    const resolve = confirmResolve.current
    confirmResolve.current = null
    resolve?.(ok)
  }
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  // A reference in the URL, which is what a mention email now links to.
  //
  // Resolved through its own endpoint rather than by looking for the reference in
  // the board this page already holds. The board is whatever the current filters
  // returned, so finding it there would work when the filters happened to include
  // it and silently fail when they did not, which is the worst kind of link:
  // one that works when you test it and not when somebody needs it.
  const { ref: refParam } = useParams<{ ref: string }>()
  const navigate = useNavigate()
  const refLookup = useResolveTaskRef<{ id: string; ref: number; title: string } | null>()
  const refTrigger = refLookup.trigger
  const [refMissing, setRefMissing] = useState<string | null>(null)
  const [addGroup, setAddGroup] = useState<BoardGroup | null>(null)
  // The reverse question the links exist to answer: what is outstanding on this
  // car, this owner, this driver. One at a time, because the question is always
  // asked about one thing.
  const [linkFilter, setLinkFilter] = useState<{
    kind: LinkKind
    id: string
    label: string
  } | null>(null)

  const board = useGetTaskBoard()
  const peopleQuery = useGetTaskPeople()
  const runsQuery = useGetTaskIngestRuns()
  const linkOptionsQuery = useGetTaskLinkOptions()
  const updateTask = useUpdateTask()
  const setTaskLinks = useSetTaskLinks()

  const boardTrigger = board.trigger
  const peopleTrigger = peopleQuery.trigger
  const runsTrigger = runsQuery.trigger
  const linkOptionsTrigger = linkOptionsQuery.trigger

  // Filters are applied server-side so the counts on this screen are counts of
  // what was asked for, not of a page-sized slice of it.
  const load = useCallback(() => {
    void boardTrigger(
      {
        assignees,
        onlyOverdue,
        search: search.trim() || undefined,
        namesMe: namesMe || undefined,
        vehicleIds: linkFilter?.kind === 'vehicle' ? [linkFilter.id] : undefined,
        ownerIds: linkFilter?.kind === 'owner' ? [linkFilter.id] : undefined,
        driverIds: linkFilter?.kind === 'driver' ? [linkFilter.id] : undefined,
        // Server-side, like every other filter here, so the counts on this
        // screen stay counts of what was asked for.
        ...dateFilterParams(dateFilter),
      },
      { skipCache: true },
    )
    void peopleTrigger({}, { skipCache: true })
  }, [boardTrigger, peopleTrigger, assignees, onlyOverdue, search, namesMe, linkFilter, dateFilter])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  // The pickers are 330 rows and they change when a car or a driver is
  // onboarded, not while somebody is looking at the board, so they are fetched
  // once per visit rather than on every reload.
  useEffect(() => {
    if (canView) void linkOptionsTrigger({}, { skipCache: true })
  }, [canView, linkOptionsTrigger])

  useEffect(() => {
    if (canView && view === 'meetings') void runsTrigger({}, { skipCache: true })
  }, [canView, view, runsTrigger])

  // Open what the link named. Runs on the reference rather than on every render,
  // so closing the panel does not immediately reopen it.
  useEffect(() => {
    if (!canView || !refParam) return
    let cancelled = false
    void (async () => {
      const found = (await refTrigger({ ref: refParam }, { skipCache: true })) as
        | { id: string }
        | null
        | undefined
      if (cancelled) return
      if (found?.id) {
        setRefMissing(null)
        setOpenTaskId(found.id)
        setView('board')
      } else {
        // Said plainly. A link that silently lands you on an unfiltered board
        // makes the person think the email was wrong about the number, and then
        // they stop trusting the emails.
        setRefMissing(refParam)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [canView, refParam, refTrigger])

  const rows = (board.data as TaskRow[] | undefined) ?? []
  const people = (peopleQuery.data as TaskPerson[] | undefined) ?? []
  const runs = (runsQuery.data as IngestRun[] | undefined) ?? []
  const linkOptions = (linkOptionsQuery.data as TaskLinkOption[] | undefined) ?? []

  const counts = useMemo(() => {
    // matched_filter false means the row came back only to give a subtask
    // somewhere to sit, so it is on the screen without having been asked for.
    // Counting it would inflate every figure in the strip the moment a filter
    // pulled in one parent. undefined is treated as matched, because this page
    // can be deployed before the handler that reports it and a board that
    // counts nothing is worse than one that counts a little too much.
    const top = rows.filter((r) => !r.parent_task_id && r.matched_filter !== false)
    return {
      total: top.length,
      open: top.filter((r) => r.is_open).length,
      overdue: top.filter((r) => r.is_overdue).length,
      // Named for what it is. These tasks have not been measured, and calling
      // them anything softer would hide the gap the board has always had.
      noTarget: top.filter((r) => r.is_open && r.target_completion === null).length,
      conflicts: top.filter((r) => r.group_status_conflict).length,
    }
  }, [rows])

  // Filtered client-side, unlike the others, because the conflict is a property
  // of the row rather than a question worth another round trip.
  const visibleRows = useMemo(
    () => (onlyConflicts ? rows.filter((r) => r.group_status_conflict || r.parent_task_id) : rows),
    [rows, onlyConflicts],
  )

  const save = useCallback(
    async (taskId: string, patch: Record<string, unknown>) => {
      // Closing a parent that still has open subtasks asks first.
      //
      // Here rather than in StatusCell because this is the one place every field
      // change on the board passes through, so the drag, the pill and the panel
      // all get the same guard without three copies of it. It asks rather than
      // refuses: a subtask can be genuinely moot, and a board that will not let
      // you finish something is a board people work around.
      //
      // subitem_count and subitem_done_count come from v_task_board, which
      // derives them from parent_task_id. done means status = 'Done', so a
      // cancelled subtask reads as open here. That is stated in the dialog
      // rather than guessed at, which is why it names the numbers.
      const closing =
        patch.status === 'Done' ||
        patch.status === 'Cancelled' ||
        patch.board_group === 'Completed' ||
        patch.board_group === 'Cancelled'
      if (closing) {
        const row = rows.find((r) => r.id === taskId)
        const total = row ? Number(row.subitem_count ?? 0) : 0
        const done = row ? Number(row.subitem_done_count ?? 0) : 0
        const stillOpen = total - done
        if (row && stillOpen > 0) {
          const ok = await new Promise<boolean>((resolve) => {
            confirmResolve.current = resolve
            setCloseWarning({ title: row.title, open: stillOpen, total })
          })
          if (!ok) return
        }
      }

      // Nothing on the board moves until the server has answered. The control
      // itself keeps what was chosen while it saves, so it never snaps back;
      // see useSaver in components/tasks/TaskCells.tsx.
      await runConfirmed(
        () => updateTask.trigger({ task_id: taskId, patch }),
        (confirmed) => {
          const row = (confirmed ?? null) as Record<string, unknown> | null
          const missed = unappliedFields(patch, row)
          if (missed.length > 0) {
            // The server accepted it and the row came back unchanged. Say so.
            // This is the shape of the jsonb defect of 23 August, which was
            // invisible for two days because the only symptom was the card
            // quietly sliding back.
            toast.error('That change was accepted but not saved', {
              description: `The server returned this task with ${missed.join(' and ')} unchanged. Nothing was written. Please report this rather than trying again.`,
            })
          }
          return taskRowConfirmed(taskId, row)
        },
      )
      load()
    },
    [updateTask, load, rows],
  )

  // Closing the panel also drops the reference from the address bar. Without it
  // the URL still says /tasks/27, and the effect above would put the panel back
  // the next time anything re-rendered: the person would close it and watch it
  // reopen, which reads as the screen fighting them.
  const closePanel = useCallback(() => {
    setOpenTaskId(null)
    setRefMissing(null)
    if (refParam) navigate('/tasks', { replace: true })
  }, [refParam, navigate])

  const saveLinks = useCallback(
    async (taskId: string, kind: LinkKind, ids: string[]) => {
      await setTaskLinks.trigger({ task_id: taskId, kind, ids })
      load()
    },
    [setTaskLinks, load],
  )

  // Double-clicking a chip asks "what else is open on this one". The label is
  // kept alongside the id so the chip that appears says a registration number
  // rather than a uuid.
  const filterByLink = useCallback(
    (kind: LinkKind, id: string) => {
      const found = linkOptions.find((o) => o.kind === kind && o.id === id)
      setLinkFilter({ kind, id, label: found?.label ?? 'this record' })
    },
    [linkOptions],
  )

  const toggleAssignee = (name: string) =>
    setAssignees((a) => (a.includes(name) ? a.filter((x) => x !== name) : [...a, name]))

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            The task board is available to signed-in Fleetly staff.
          </p>
          <p className="text-xs text-muted-foreground">
            Ask an admin to add you to cockpit access if you should be able to see your tasks.
          </p>
        </div>
      </div>
    )
  }

  const closeDialog = (
    <AlertDialog open={closeWarning !== null} onOpenChange={(o) => !o && answerCloseWarning(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Close this with subtasks still open?</AlertDialogTitle>
          <AlertDialogDescription>
            {closeWarning
              ? `"${closeWarning.title}" has ${closeWarning.open} of ${closeWarning.total} subtasks not marked Done. Closing the parent leaves them open underneath it, where nobody is likely to look again.`
              : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => answerCloseWarning(false)}>
            Leave it open
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => answerCloseWarning(true)}>
            Close it anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return (
    <TooltipProvider delayDuration={150}>
      {closeDialog}
      <div className="flex h-full min-h-0 flex-col bg-card">
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
              <CheckSquare className="h-5 w-5" />
            </div>
            <h1 className="text-lg font-semibold text-foreground">Tasks</h1>
            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tasks"
                  className="h-8 w-52 pl-7 text-sm"
                />
              </div>
              <Button size="sm" className="h-8" onClick={() => setAddGroup('Open')}>
                New task
              </Button>
            </div>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-border bg-background p-0.5">
              {VIEWS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setView(v.value)}
                  className={cn(
                    'rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                    view === v.value
                      ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {v.label}
                </button>
              ))}
            </div>

            <TaskDateFilters
              value={dateFilter}
              onChange={setDateFilter}
              noStartCount={rows.filter((r) => !r.start_date).length}
              noTargetCount={rows.filter((r) => !r.target_completion).length}
              backlogHeldCount={backlogHeldCount(rows, dateFilter)}
            />

            <Chip tone="neutral">
              {counts.open}
              <span className="ml-1 opacity-70">open</span>
            </Chip>
            <button type="button" onClick={() => setOnlyOverdue((o) => !o)}>
              <Chip tone={onlyOverdue ? 'red' : counts.overdue > 0 ? 'amber' : 'neutral'}>
                {counts.overdue}
                <span className="ml-1 opacity-70">overdue</span>
              </Chip>
            </button>
            {/* Assigned to me, or naming me.
                Added 2 September 2026. Before it, a task somebody had handed to
                you in an update was invisible unless you happened to open it:
                the only person filter on this board was the assignee list, and
                being named in a thread is not being assigned. */}
            <button type="button" onClick={() => setNamesMe((n) => !n)}>
              <Chip tone={namesMe ? 'brand' : 'neutral'}>
                <AtSign className="mr-1 inline h-3 w-3" />
                names me
              </Chip>
            </button>
            {/* Named plainly rather than hidden. An open task with no target
                date is the board's oldest weakness: 111 of the 253 imported
                rows had no source meeting and only 145 had a target. */}
            <Chip tone={counts.noTarget > 0 ? 'amber' : 'neutral'}>
              {counts.noTarget}
              <span className="ml-1 opacity-70">open with no target date</span>
            </Chip>
            {/* A card filed as finished whose work is not, or the reverse. The
                import preserved 18 of these rather than picking a winner, and
                one of them is an unsubmitted insurance claim. */}
            {counts.conflicts > 0 && (
              <button type="button" onClick={() => setOnlyConflicts((c) => !c)}>
                <Chip tone={onlyConflicts ? 'red' : 'amber'}>
                  {counts.conflicts}
                  <span className="ml-1 opacity-70">group and status disagree</span>
                </Chip>
              </button>
            )}
            <Chip tone="neutral">
              {counts.total}
              <span className="ml-1 opacity-70">shown</span>
            </Chip>

            {assignees.length > 0 && (
              <button
                type="button"
                onClick={() => setAssignees([])}
                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
                {assignees.join(', ')}
              </button>
            )}

            {/* The board narrowed to one car, one owner or one driver. Named by
                its registration or its name, never by an id: a filter chip
                nobody can read is a filter nobody turns off. */}
            {linkFilter && (
              <button
                type="button"
                onClick={() => setLinkFilter(null)}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-[hsl(var(--brand-soft))]/40 px-2.5 py-0.5 text-xs text-foreground"
              >
                <X className="h-3 w-3" />
                {linkFilter.kind === 'vehicle'
                  ? 'Vehicle'
                  : linkFilter.kind === 'owner'
                    ? 'Owner'
                    : 'Driver'}
                : {linkFilter.label}
              </button>
            )}
          </div>
        </div>

        {refMissing && (
          <div className="flex items-center justify-between gap-3 border-b border-border bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            <span>
              Task #{refMissing} was not found. It may have been deleted, or the link may have the
              wrong number in it. Nothing else on this board has changed.
            </span>
            <button
              type="button"
              onClick={closePanel}
              className="shrink-0 rounded-md border border-amber-400 px-3 py-1 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900"
            >
              Dismiss
            </button>
          </div>
        )}

        {board.error && view === 'board' && (
          <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
            {board.error}
          </div>
        )}

        <div className="flex min-h-0 flex-1 lg:flex-row">
          <div className="min-h-0 min-w-0 flex-1 overflow-auto">
            {view === 'board' && (
              <>
                {board.loading && rows.length === 0 && (
                  <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
                    <Spinner className="h-4 w-4" /> Loading the board…
                  </div>
                )}
                {!board.loading && visibleRows.length === 0 && (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    Nothing matches. Clear the filters, or add the first task.
                  </div>
                )}
                <TaskBoard
                  rows={visibleRows}
                  canEdit={canView}
                  selectedId={openTaskId}
                  people={people}
                  linkOptions={linkOptions}
                  onOpen={setOpenTaskId}
                  onSave={save}
                  onSaveLinks={saveLinks}
                  onFilterLink={filterByLink}
                  onAddInGroup={setAddGroup}
                />
              </>
            )}

            {view === 'people' && (
              <TaskPeople people={people} selected={assignees} onSelect={toggleAssignee} />
            )}

            {view === 'meetings' && (
              <MeetingRuns
                runs={runs}
                isAdmin={isAdmin}
                onChanged={() => {
                  void runsTrigger({}, { skipCache: true })
                  load()
                }}
              />
            )}
          </div>

          {openTaskId && (
            <div className="hidden w-[28rem] shrink-0 border-l border-border lg:block">
              <TaskPanel
                taskId={openTaskId}
                canEdit={canView}
                people={people}
                linkOptions={linkOptions}
                onClose={closePanel}
                onChanged={load}
              />
            </div>
          )}
        </div>

        {/* Below lg the panel takes the whole screen rather than squeezing into
            a column too narrow to read a conversation in. */}
        {openTaskId && (
          <div className="fixed inset-0 z-40 bg-card lg:hidden">
            <TaskPanel
              taskId={openTaskId}
              canEdit={canView}
              people={people}
              linkOptions={linkOptions}
              onClose={closePanel}
              onChanged={load}
            />
          </div>
        )}

        <NewTaskDialog
          open={addGroup !== null}
          group={addGroup ?? 'Open'}
          people={people}
          linkOptions={linkOptions}
          onOpenChange={(o) => setAddGroup(o ? addGroup : null)}
          onCreated={load}
        />
      </div>
    </TooltipProvider>
  )
}
